import { AxleIconButton } from '@/components/buttons/AxleIconButton';
import { FloatingActionButton } from '@/components/buttons/FloatingActionButton';
import { NONE_TEXT } from '@/components/displayFields/AxleInfoField';
import { AxleChip, ChipsVerticalStack, QualificationChips } from '@/components/displayFields/chips';
import { AxlePageHeader } from '@/components/layout/AxlePageHeader';
import { AxleTable } from '@/components/tables/AxleTable';
import { If } from '@/components/utils/conditionalComponents';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { useAppConfig } from '@/fetching/api/appConfig';
import { SupplyConnection, useSupplyConnections } from '@/fetching/api/supplyAndDemandConnections';
import AddIcon from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';

export const useShowSupplyConnections = (): boolean => {
    const appConfig = useAppConfig();
    const { allSupplyConnections } = useSupplyConnections();

    return (
        appConfig.downstream_handshake.page &&
        // Hide supply connections if there are no supply connections, nor can the user create one.
        (allSupplyConnections.length > 0 || appConfig.downstream_handshake.create)
    );
};

export const SupplyConnections = () => {
    const { openDialog } = useDialog();

    const appConfig = useAppConfig();
    const showSupplyConnections = useShowSupplyConnections();
    const { allSupplyConnections } = useSupplyConnections();

    // Opening the dialog is what carries the connection, so the table holds no selection.
    const setSupplyConnectionToEdit = (supplyConnection: SupplyConnection) => {
        openDialog(DialogType.EditSupplyConnection, { supplyConnection });
    };

    return (
        <If condition={showSupplyConnections}>
            <AxlePageHeader title={'Supply Connections'}>
                <If condition={appConfig.downstream_handshake.create}>
                    <FloatingActionButton
                        icon={<AddIcon />}
                        text={'Create Connection'}
                        onClick={() => openDialog(DialogType.CreateConnection, {})}
                    />
                </If>
            </AxlePageHeader>
            <AxleTable
                data={allSupplyConnections}
                elevation={1}
                columns={[
                    {
                        label: 'Organization',
                        field: 'handshake.from_org.name',
                    },
                    {
                        label: 'Supply Connection',
                        field: 'handshake.to_org.name',
                    },
                    {
                        label: 'Org-level qualifications',
                        rowToComponent: (supplyConnection: SupplyConnection) => (
                            <QualificationChips
                                qualifications={supplyConnection.org_qualifications}
                            />
                        ),
                    },
                    {
                        label: 'Individual-level Qualifications',
                        rowToComponent: (supplyConnection: SupplyConnection) => (
                            <QualificationChips
                                qualifications={supplyConnection.passthrough_qualifications}
                            />
                        ),
                    },
                    {
                        label: 'Territory restrictions',
                        rowToComponent: (supplyConnection: SupplyConnection) =>
                            supplyConnection.handshake.has_territory_restriction ? (
                                <ChipsVerticalStack>
                                    {supplyConnection.handshake.territories.map((territory) => (
                                        <AxleChip key={territory.uuid} label={territory.name} />
                                    ))}
                                </ChipsVerticalStack>
                            ) : (
                                <>{NONE_TEXT}</>
                            ),
                    },
                    {
                        rowToComponent: (supplyConnection: SupplyConnection) => (
                            <AxleIconButton
                                icon={<Edit />}
                                tooltip={'Edit Supply Connection'}
                                onClick={() => setSupplyConnectionToEdit(supplyConnection)}
                            />
                        ),
                    },
                ]}
            />
        </If>
    );
};
